/**
 * The club leaderboard — FR-STAT-2, FR-STAT-3, BR-PAIR-5.
 *
 * The service and the endpoint have existed since BUILD_PLAN 1.16; what was
 * missing was a door. Without the bottom navigation there was nowhere to put
 * one, so all-time standings were computed on every commit and never seen.
 *
 * BR-PAIR-5 is the rule that shapes the screen: ranking requires 20 games, and
 * players below the threshold appear in a separate list — **never hidden**. In
 * a club where somebody joins in March, being absent from the table and being
 * bottom of it are very different messages, and only one of them is true.
 *
 * BR-COMMIT-2 also applies, quietly: only committed sessions are in here, so
 * today's games are not, and the screen says so rather than leaving a member
 * to wonder why this morning did not move anything.
 */

import { useEffect, useState } from "react";
import { api, type BreakdownReport, type Leaderboard, type LeaderboardRow } from "../api.js";
import { PRIMARY_CLASS, SORTED_DESC, rankText } from "../statsPresentation.js";
import { PairProfile, PlayerProfile } from "./PlayerProfile.js";

type Period = {
  label: string;
  params: { days?: number; sessions?: number; month?: string; year?: number };
};

/**
 * FR-STAT-2, FR-STAT-3 — all four shapes the endpoint offers.
 *
 * "This month", "the last month" and "the last few sessions" are genuinely
 * different questions in a club that sometimes plays twice a week and
 * sometimes not for a fortnight, and a calendar month is the one the club
 * actually argues about. The month and year options are computed from the
 * server's data rather than the device clock — see `periodsFor`.
 */
function periodsFor(months: string[]): Period[] {
  const years = [...new Set(months.map((m) => m.slice(0, 4)))];
  return [
    { label: "All time", params: {} },
    ...months.slice(0, 3).map((month) => ({ label: monthLabel(month), params: { month } })),
    ...years.slice(0, 2).map((year) => ({ label: year, params: { year: Number(year) } })),
    { label: "Last 30 days", params: { days: 30 } },
    { label: "Last 5 sessions", params: { sessions: 5 } },
  ];
}

/** `2026-08` as "Aug 2026", without constructing a Date from a bare string. */
function monthLabel(month: string): string {
  const [year, index] = month.split("-");
  const names = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  return `${names[Number(index) - 1] ?? month} ${year}`;
}

export function ClubBoard(): React.JSX.Element {
  const [period, setPeriod] = useState(0);
  const [gameType, setGameType] = useState<"doubles" | "singles">("doubles");
  const [board, setBoard] = useState<Leaderboard | null>(null);
  const [breakdown, setBreakdown] = useState<BreakdownReport | null>(null);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [profileOf, setProfileOf] = useState<string | null>(null);
  const [pairOf, setPairOf] = useState<[string, string] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Which months exist comes from the data, so the chips never offer a month
  // the club did not play in — and never miss one it did.
  const periods = periodsFor(breakdown?.months.map((m) => m.month) ?? []);

  useEffect(() => {
    api.breakdown(gameType, 24).then(setBreakdown).catch(() => undefined);
  }, [gameType]);

  useEffect(() => {
    setBoard(null);
    setError(null);
    api
      .leaderboard({ gameType, ...(periods[period]?.params ?? {}) })
      .then(setBoard)
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : "Could not load the leaderboard."),
      );
    // Depends on `breakdown` rather than on `periods`, which is derived from
    // it: a new array identity every render would refetch the board on every
    // render. (No exhaustive-deps rule is configured here, so this is a note
    // for a reader rather than a silenced warning.)
  }, [period, gameType, breakdown]);

  if (pairOf) {
    return (
      <PairProfile playerA={pairOf[0]} playerB={pairOf[1]} onClose={() => setPairOf(null)} />
    );
  }

  if (profileOf) {
    return (
      <PlayerProfile
        playerId={profileOf}
        onClose={() => setProfileOf(null)}
        // Tapping a partner's name opens the partnership, which is the moment
        // the question "how do we do together?" actually comes up.
        onOpenPair={(a, b) => setPairOf([a, b])}
      />
    );
  }

  return (
    <>
      <div className="eyebrow">Committed sessions only</div>
      <h1 className="display">The club</h1>

      <div className="row" style={{ marginTop: ".6rem" }}>
        {periods.map((p, index) => (
          <button
            key={p.label}
            className={index === period ? "chip chip-on" : "chip"}
            onClick={() => setPeriod(index)}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* BR-STAT-3 — separate universes, so this is a switch and never a total. */}
      <div className="row" style={{ marginTop: ".5rem" }}>
        {(["doubles", "singles"] as const).map((t) => (
          <button
            key={t}
            className={t === gameType ? "chip chip-on" : "chip"}
            onClick={() => setGameType(t)}
          >
            {t}
          </button>
        ))}
      </div>

      {error ? <p className="error">{error}</p> : null}
      {!board && !error ? <p className="muted small">Loading…</p> : null}

      {board ? (
        <>
          <BoardTable rows={board.ranked} onOpen={setProfileOf} />
          {board.ranked.length === 0 ? (
            <p className="muted small">
              Nobody has {board.qualifyingGames} {gameType} games in this period
              yet.
            </p>
          ) : null}

          {/* BR-PAIR-5 — shown, always, with the reason they are not ranked. */}
          {board.notYetQualified.length > 0 ? (
            <>
              <h2>Not yet qualified</h2>
              <p className="muted small">
                {board.qualifyingGames} games needed to be ranked. These figures
                are real; they simply are not enough to place on yet.
              </p>
              <BoardTable rows={board.notYetQualified} showProgress onOpen={setProfileOf} />
            </>
          ) : null}

          {/* FR-STAT-3 — the same figures over time rather than in total. */}
          {breakdown && breakdown.months.length > 0 ? (
            <>
              <button className="quiet" onClick={() => setShowBreakdown(!showBreakdown)}>
                {showBreakdown ? "Hide month by month" : "Show month by month"}
              </button>
              {showBreakdown ? <Breakdown report={breakdown} /> : null}
            </>
          ) : null}

          <p className="note">
            Only committed sessions count (BR-COMMIT-2), so today&apos;s games
            appear here after the session is finished. Tap a name for their
            profile.
          </p>
        </>
      ) : null}
    </>
  );
}

function BoardTable({
  rows,
  showProgress,
  onOpen,
}: {
  rows: LeaderboardRow[];
  showProgress?: boolean;
  onOpen?: (playerId: string) => void;
}): React.JSX.Element | null {
  if (rows.length === 0) return null;

  return (
    <div className="table-scroll">
      <table className="standings">
        <thead>
          <tr>
            {/* FR-STAT-17 — reported from the server, which shares a position
                between tied players rather than breaking the tie by name. */}
            <th scope="col" className="rank" aria-label="Rank">
              #
            </th>
            <th scope="col" className="sticky-col">
              Player
            </th>
            <th scope="col">G</th>
            <th scope="col">W</th>
            <th scope="col">Win%</th>
            <th scope="col">PW</th>
            <th scope="col">PL</th>
            <th scope="col">Dom</th>
            <th scope="col">Dom%</th>
            <th scope="col" className={PRIMARY_CLASS} aria-sort="descending">
              MP<span className="sortmark">{SORTED_DESC}</span>
            </th>
            {showProgress ? <th scope="col">To rank</th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, index) => (
            <tr key={r.playerId} className={index === 0 && !showProgress ? "leader" : undefined}>
              <td className="rank">{rankText(r.rank, r.shared)}</td>
              <th scope="row" className="sticky-col">
                <button className="linkish" onClick={() => onOpen?.(r.playerId)}>
                  {r.displayName}
                </button>
                {r.isGuest ? <span className="guest-flag">guest</span> : null}
              </th>
              <td>{r.games}</td>
              <td>{r.wins}</td>
              <td>{pct(r.winPct)}</td>
              <td>{r.pointsWon}</td>
              <td>{r.pointsLost}</td>
              <td className={sign(r.dominance)}>{dec(r.dominance)}</td>
              <td className={sign(r.normalisedDominance)}>{pct(r.normalisedDominance)}</td>
              <td className={PRIMARY_CLASS}>{r.matchPoints}</td>
              {showProgress ? <td>{r.qualificationProgress ?? "—"}</td> : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** BR-STAT-1 — an em dash where there is nothing to divide by. */
function pct(value: number | null): string {
  return value === null ? "—" : `${(value * 100).toFixed(1)}%`;
}

function dec(value: number | null): string {
  return value === null ? "—" : value.toFixed(2);
}

function sign(value: number | null): string | undefined {
  if (value === null || value === 0) return undefined;
  return value > 0 ? "pos" : "neg";
}

/**
 * FR-STAT-3 — a month per block, newest first.
 *
 * Deliberately not one wide table with a column per month: at 2.2 sessions a
 * month the club will have thirty of them after the migration, and a
 * thirty-column table on a phone is a table nobody reads. Blocks scroll.
 */
function Breakdown({ report }: { report: BreakdownReport }): React.JSX.Element {
  return (
    <>
      {report.months.map((m) => (
        <section key={m.month} className="block">
          <h3 className="card-head">
            {monthLabel(m.month)}
            <span className="muted small">
              {m.rows.length} {m.rows.length === 1 ? "player" : "players"}
            </span>
          </h3>
          <div className="table-scroll">
            <table className="standings">
              <thead>
                <tr>
                  <th scope="col" className="sticky-col">
                    Player
                  </th>
                  <th scope="col">Sess</th>
                  <th scope="col">G</th>
                  <th scope="col">W</th>
                  <th scope="col">Win%</th>
                  <th scope="col">MP</th>
                </tr>
              </thead>
              <tbody>
                {m.rows.map((r) => (
                  <tr key={r.playerId}>
                    <th scope="row" className="sticky-col">
                      {r.displayName}
                    </th>
                    <td>{r.sessionsPlayed}</td>
                    <td>{r.games}</td>
                    <td>{r.wins}</td>
                    <td>{pct(r.winPct)}</td>
                    <td className="strong">{r.matchPoints}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </>
  );
}
